import type { Span } from '@sentry/core'
import { Sentry } from '@/shared/lib/sentry'
import { ensureConsultationTraceId } from '@/shared/observability/trace-context'
import { SENTRY_SYNAP_METADATA_BASE } from '@/shared/observability/telemetry-metadata'
import { runSentrySafely } from '@/shared/monitoring/sentry.helpers'
import { sanitizeSentryAnamnesisContext } from '@/shared/monitoring/telemetry-sanitizer'

/** Transações de performance do pipeline de anamnese (AC #3). */
export const ANAMNESIS_SENTRY_TRANSACTIONS = {
  START_RECORDING: 'start_recording',
  CHUNK_PROCESSING: 'chunk_processing',
  TRANSCRIPTION_PIPELINE: 'transcription_pipeline',
  AI_ANALYSIS_PIPELINE: 'ai_analysis_pipeline',
  DIARIZATION_PIPELINE: 'diarization_pipeline',
  INSIGHT_RENDER: 'insight_render',
  FINAL_SUMMARY_PIPELINE: 'final_summary_pipeline',
  CONSULTATION_ROOT: 'anamnesis_consultation',
} as const

export type AnamnesisSentryTransactionName =
  (typeof ANAMNESIS_SENTRY_TRANSACTIONS)[keyof typeof ANAMNESIS_SENTRY_TRANSACTIONS]

const consultationRootSpans = new Map<string, Span>()
let activeConsultationId: string | null = null

export const isSentryClientReady = (): boolean =>
  typeof Sentry.startSpan === 'function' && typeof Sentry.getTraceData === 'function'

export const getSentryTracePropagationHeaders = (): Record<string, string> | null =>
  runSentrySafely(() => {
    if (!isSentryClientReady()) return null
    const mod = Sentry as typeof import('@sentry/react')
    const traceData = mod.getTraceData?.()
    if (!traceData || typeof traceData !== 'object') return null
    const headers: Record<string, string> = {}
    const sentryTrace = (traceData as { 'sentry-trace'?: string })['sentry-trace']
    const baggage = (traceData as { baggage?: string }).baggage
    if (sentryTrace?.trim()) headers['sentry-trace'] = sentryTrace.trim()
    if (baggage?.trim()) headers.baggage = baggage.trim()
    return Object.keys(headers).length ? headers : null
  }) ?? null

export const bindAnamnesisSentryConsultation = (options: {
  consultationId: string
  doctorId?: string | null
  language?: string
  offlineMode?: boolean
  fallbackMode?: string | null
}): void => {
  runSentrySafely(() => {
    const consultationId = options.consultationId.trim()
    if (!consultationId || !isSentryClientReady()) return

    activeConsultationId = consultationId
    const langfuseTraceId = ensureConsultationTraceId(consultationId)
    const context = sanitizeSentryAnamnesisContext({
      consultation_id: consultationId,
      langfuse_trace_id: langfuseTraceId,
      language: options.language ?? 'pt-BR',
      offline_mode: options.offlineMode ?? false,
      fallback_mode: options.fallbackMode ?? null,
      doctor_id: options.doctorId ?? null,
    })

    Sentry.setTag?.('consultation_id', consultationId)
    Sentry.setTag?.('feature', 'anamnesis-realtime')
    if (options.doctorId?.trim()) {
      Sentry.setTag?.('doctor_id', options.doctorId.trim())
    }
    Sentry.setContext?.('anamnesis_consultation', {
      ...SENTRY_SYNAP_METADATA_BASE,
      ...context,
    })

    const mod = Sentry as typeof import('@sentry/react')
    if (typeof mod.startInactiveSpan !== 'function') return
    const existing = consultationRootSpans.get(consultationId)
    if (existing) return

    const rootSpan = mod.startInactiveSpan({
      name: ANAMNESIS_SENTRY_TRANSACTIONS.CONSULTATION_ROOT,
      op: 'anamnesis.session',
      attributes: {
        consultation_id: consultationId,
        langfuse_trace_id: langfuseTraceId,
      },
    })
    if (rootSpan) consultationRootSpans.set(consultationId, rootSpan)
  })
}

export const unbindAnamnesisSentryConsultation = (consultationId?: string): void => {
  runSentrySafely(() => {
    const id = consultationId?.trim() || activeConsultationId
    if (!id) {
      activeConsultationId = null
      return
    }
    const root = consultationRootSpans.get(id)
    root?.end()
    consultationRootSpans.delete(id)
    if (activeConsultationId === id) activeConsultationId = null
    Sentry.setTag?.('consultation_id', '')
  })
}

const applyConsultationScope = (
  consultationId: string,
  chunkId: string | undefined,
  extra: Record<string, string | number | boolean | null | undefined>,
): void => {
  Sentry.setTag?.('consultation_id', consultationId)
  if (chunkId?.trim()) Sentry.setTag?.('chunk_id', chunkId.trim())
  const sanitized = sanitizeSentryAnamnesisContext(extra)
  if (Object.keys(sanitized).length) {
    Sentry.setContext?.('anamnesis_event', sanitized)
  }
}

export const withAnamnesisSentrySpan = <T>(
  name: AnamnesisSentryTransactionName,
  options: {
    consultationId?: string | null
    chunkId?: string | null
    metadata?: Record<string, string | number | boolean | null | undefined>
  },
  run: () => T,
): T => {
  const consultationId =
    options.consultationId?.trim() || activeConsultationId || undefined
  if (!consultationId || !isSentryClientReady()) {
    return run()
  }

  try {
    return (
      Sentry.startSpan?.(
        {
          name,
          op: 'anamnesis.pipeline',
          attributes: {
            consultation_id: consultationId,
            ...(options.chunkId?.trim() ? { chunk_id: options.chunkId.trim() } : {}),
            ...sanitizeSentryAnamnesisContext(options.metadata ?? {}),
          },
        },
        () => {
          applyConsultationScope(consultationId, options.chunkId ?? undefined, options.metadata ?? {})
          return run()
        },
      ) ?? run()
    )
  } catch {
    return run()
  }
}

export const withAnamnesisSentrySpanAsync = async <T>(
  name: AnamnesisSentryTransactionName,
  options: {
    consultationId?: string | null
    chunkId?: string | null
    metadata?: Record<string, string | number | boolean | null | undefined>
  },
  run: () => Promise<T>,
): Promise<T> => {
  const consultationId =
    options.consultationId?.trim() || activeConsultationId || undefined
  if (!consultationId || !isSentryClientReady()) {
    return run()
  }

  return Sentry.startSpan?.(
    {
      name,
      op: 'anamnesis.pipeline',
      attributes: {
        consultation_id: consultationId,
        ...(options.chunkId?.trim() ? { chunk_id: options.chunkId.trim() } : {}),
        ...sanitizeSentryAnamnesisContext(options.metadata ?? {}),
      },
    },
    async () => {
      applyConsultationScope(consultationId, options.chunkId ?? undefined, options.metadata ?? {})
      return run()
    },
  ) as Promise<T>
}

export const captureAnamnesisSentryMessage = (
  message: string,
  level: 'info' | 'warning' | 'error',
  options: {
    consultationId?: string | null
    chunkId?: string | null
    event?: string
    metadata?: Record<string, string | number | boolean | null | undefined>
  },
): void => {
  runSentrySafely(() => {
    const consultationId =
      options.consultationId?.trim() || activeConsultationId || undefined
    if (consultationId) applyConsultationScope(consultationId, options.chunkId ?? undefined, options.metadata ?? {})
    Sentry.captureMessage?.(message, {
      level,
      tags: {
        feature: 'anamnesis-realtime',
        ...(options.event ? { event: options.event } : {}),
        ...(consultationId ? { consultation_id: consultationId } : {}),
        ...(options.chunkId?.trim() ? { chunk_id: options.chunkId.trim() } : {}),
      },
      extra: sanitizeSentryAnamnesisContext(options.metadata ?? {}),
    })
  })
}
